/* eslint-disable no-useless-escape */

/**
 * check if value !== undefined && !== null
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (value as T) !== undefined && (value as T) !== null;
}

export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

/**
 * check if value !== undefined && !== null && !== ''
 */
export function isStringDefined(value: string | null | undefined): value is string {
  return isDefined(value) && value !== '';
}

/**
 * Checks if an array is not empty.
 * @param value the array to validate.
 */
export function isArrayNotEmpty<T>(value: T[] | null | undefined): value is T[] {
  return isDefined(value) && Array.isArray(value) && value.length > 0;
}

/**
 * Returns the input string if it's defined, or undefined otherwise.
 * @param value - The string value to check
 * @returns The original string if defined, or undefined
 */
export function getStringIfDefined(value: string | undefined | null): string | undefined {
  return isStringDefined(value) ? value : undefined;
}

/**
 * Asserts an array is not empty.
 * @param value the array to validate.
 * @param error the custom error message to throw.
 */
export function assertIsArrayNotEmpty<T>(value: T[] | undefined, error?: Error | string): asserts value is T[] {
  if (!isArrayNotEmpty(value)) {
    if (!isDefined(error)) {
      throw new Error('Array value is empty');
    }

    if (typeof error === 'string') {
      throw new Error(error);
    }

    throw error;
  }
}

/**
 * Assert a string is defined and not empty
 */
export function assertIsStringDefined(value: string | undefined, error?: Error | string): asserts value is string {
  if (!isStringDefined(value)) {
    if (!isDefined(error)) {
      throw new Error('String value is not defined');
    }

    if (typeof error === 'string') {
      throw new Error(error);
    }

    throw error;
  }
}

/**
 * Assert a value is defined
 */
export function assertIsDefined<T>(value: T | undefined, error?: Error | string): asserts value is T {
  if (!isDefined(value)) {
    if (!isDefined(error)) {
      throw new Error('Value is not defined');
    }

    if (typeof error === 'string') {
      throw new Error(error);
    }

    throw error;
  }
}

/**
 * Check if a property is set on a object
 *
 * ```
 * const test = {toto: undefined};
 * hasOwnProperty(test, 'toto') // true
 *
 * const test = {toto: null};
 * hasOwnProperty(test, 'toto') // true
 *
 * const test = {};
 * hasOwnProperty(test, 'toto') // false
 * ```
 */
export function hasOwnProperty<T extends object>(value: object, property: keyof T): value is T {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return Object.prototype.hasOwnProperty.call(value, property);
}
/**
 * Calculates the date at midnight based on the date passed as a parameter
 * params:
 *  - date := date of the day on which you want to calculate midnight
 *  - referenceDay := date on which to calculate the time zone
 *  - start := boolean for choosing whether at the beginning of the day or at the end
 */
export const midnight = (date: Date, referenceDay: string, start: boolean): Date => {
  let pattern: RegExp = /(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2})[+](\d{2})\:(\d{2})/;
  let timezone: RegExpExecArray | null = pattern.exec(referenceDay);
  let timezoneString: string = 'Z';
  if (timezone && timezone.length === 9) {
    date.setHours(date.getHours() + parseInt(timezone[7], 10));
    date.setMinutes(date.getMinutes() + parseInt(timezone[8], 10));
    timezoneString = `+${timezone[7]}:${timezone[8]}`;
  }
  pattern = /(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2})[-](\d{2})\:(\d{2})/;
  timezone = pattern.exec(referenceDay);
  if (timezone && timezone.length === 9) {
    date.setHours(date.getHours() - parseInt(timezone[7], 10));
    date.setMinutes(date.getMinutes() - parseInt(timezone[8], 10));
    timezoneString = `-${timezone[7]}:${timezone[8]}`;
  }

  const isoDate: string = date.toISOString().split('T')[0];
  if (start) {
    return new Date(`${isoDate}T00:00:00${timezoneString}`);
  } else {
    return new Date(`${isoDate}T23:59:59${timezoneString}`);
  }
};

/**
 * check if date is >= midnight jet leg today
 */
export function IsDateGraterThanTodayMidnight(date: string): boolean {
  const d: Date = midnight(new Date(), date, true);

  return new Date(date) >= d;
}

/**
 * check if date is <= midnight jet leg today
 */
export function IsDateLessThanTodayMidnight(date: string): boolean {
  const d: Date = midnight(new Date(), date, true);

  return new Date(date) <= d;
}

/**
 * check if firstDate is >= of secondDate
 */
export function IsDateGraterThanOtherDate(firstDate: string, secondDate: string): boolean {
  return new Date(firstDate) >= new Date(secondDate);
}

/**
 * Checks if a value is a valid member of a given string enum.
 * @param enumObj - The enum object to check against.
 * @param value - The value to validate.
 * @returns True if the value is a valid enum member, false otherwise.
 */
export function isEnumValue<T extends Record<string, string>>(
  enumObj: T,
  value: unknown,
): value is T[keyof T] {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return typeof value === 'string' && Object.values(enumObj).includes(value as T[keyof T]);
}

export function isString(value: unknown): value is string {
  return typeof value === 'string' || value instanceof String;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export function isFunction(value: unknown): value is Function {
  return typeof value === 'function';
}
